/**
 * Token Utilities — Shared MSAL token acquisition for translation APIs.
 *
 * Extracts token acquisition logic so it can be reused by any service
 * that needs a Bearer token for inline translation (historical reports,
 * data objects, etc.).
 */

import { AZURE_AD_CONFIG } from '../config/env';
import { getMsalInstance } from './authenticatedHttpClient';

/**
 * Acquire a Bearer token scoped to `<CLIENT_ID>/.default`.
 *
 * Used when calling backend endpoints with `target_language` parameter,
 * which require an Authorization header for the Translation API.
 *
 * DEV ONLY: Set VITE_DEV_TRANSLATION_TOKEN in .env.local to bypass MSAL.
 */
export async function acquireTranslationToken(): Promise<string> {
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_TRANSLATION_TOKEN) {
        return import.meta.env.VITE_DEV_TRANSLATION_TOKEN as string;
    }

    const msal = getMsalInstance();
    if (!msal) {
        throw new Error('MSAL instance not available — user may not be logged in');
    }

    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) {
        throw new Error('No authenticated accounts — please log in first');
    }

    const scopes = [`${AZURE_AD_CONFIG.clientId}/.default`];

    try {
        const response = await msal.acquireTokenSilent({ scopes, account: accounts[0] });
        return response.accessToken;
    } catch {
        const response = await msal.acquireTokenPopup({ scopes });
        return response.accessToken;
    }
}
